import { api } from "../redux/api/api";
import { PERMISSION_FLAGS, PermissionNode } from "../redux/api/permissions-api";
import menuItems from "../data/menuItems.json";

/**
 * Per-user module access, from Settings → Authorization.
 *
 * The sidebar and route guards used to read only the role-level `access` flags
 * from the login response. The per-user grid saved to /user-permissions/{id}
 * was never consulted, so a practitioner granted just Task and User History
 * still saw Contacts, Booking, ABHA and Calendar — whatever their role allowed.
 *
 * The result is a list of sidebar ids (top-level and sub-item) the account may
 * open, or null when no per-user grid applies. Null keeps the role behaviour,
 * so an account whose grid was never filled in (admins, today) is not locked
 * out of everything.
 */
export const MODULE_GRANTS_KEY = "remnutri_module_grants";

/** Module names compared lowercase with punctuation and spaces removed. */
const normalise = (name: string) => (name ?? "").toLowerCase().replace(/[^a-z0-9]/g, "");

/**
 * Permission-module name -> sidebar id. Several spellings per entry because the
 * module tree is seeded by hand ("Calender" is how it is stored today).
 */
const MODULE_TO_MENU: Record<string, string> = {
  catalogue: "catalogue",
  catalog: "catalogue",
  contacts: "contacts",
  allleads: "all-leads",
  allcustomers: "all-customers",
  abha: "abha",
  salesmarketing: "sales-marketing",
  salesandmarketing: "sales-marketing",
  bookingorders: "booking-orders",
  booking: "booking-orders",
  calender: "calendar",
  calendar: "calendar",
  remnutrimeals: "remnutriMeals",
  meals: "meals",
  ingredients: "ingredients",
  weeklymealplanner: "weekly-meal-planner",
  mealmanagement: "meal-management",
  userhistory: "user-history",
  settings: "settings",
};

const isGranted = (node: PermissionNode) =>
  node.all === true || PERMISSION_FLAGS.some((flag) => node[flag] === true);

const childrenOf = (node: PermissionNode) => [...(node.modules ?? []), ...(node.sub_modules ?? [])];

/** True when this node or anything beneath it carries a granted flag. */
const grantedWithin = (node: PermissionNode): boolean =>
  isGranted(node) || childrenOf(node).some(grantedWithin);

/**
 * Sidebar ids this tree grants, or null when the tree grants nothing at all.
 *
 * A top-level entry opens when its module or any child is granted. A sub-item
 * follows its own module row when the tree has one; sub-items with no module of
 * their own (Catalogue's "All Items") follow their parent.
 */
export const menuGrantsFromTree = (mainModules: PermissionNode[] = []): string[] | null => {
  if (!mainModules.some(grantedWithin)) return null;

  // Matched by name under the right parent, since child names repeat
  // ("Appointments" sits under both Booking Orders and Catalogue).
  const granted = new Set<string>();
  const known = new Set<string>();
  for (const main of mainModules) {
    const mainId = MODULE_TO_MENU[normalise(main.module_name)];
    if (!mainId) continue;
    if (grantedWithin(main)) granted.add(mainId);
    for (const child of childrenOf(main)) {
      const childId = MODULE_TO_MENU[normalise(child.module_name)];
      if (!childId) continue;
      known.add(childId);
      if (grantedWithin(child)) granted.add(childId);
    }
  }

  for (const item of menuItems as { id: string; subItems?: { id: string }[] }[]) {
    for (const sub of item.subItems ?? []) {
      if (!known.has(sub.id) && granted.has(item.id)) granted.add(sub.id);
    }
  }
  return Array.from(granted);
};

/** The user id inside a JWT, without verifying it — it only picks whose grid to read. */
export const userIdFromToken = (token?: string | null): string | undefined => {
  try {
    const payload = token?.split(".")[1];
    if (!payload) return undefined;
    const claims = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    const id = claims?.userId ?? claims?.user_id ?? claims?.id ?? claims?.sub;
    return id ? String(id) : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Fetch this account's grants. Never throws.
 *
 * Resolves to undefined when the grid could not be read at all (route refused
 * for a non-admin token, no user id, unexpected shape) — distinct from null,
 * "read, and it grants nothing" — so a failed refresh does not wipe grants
 * that were read successfully at login.
 *
 * The token is passed explicitly because at login it is not in storage yet.
 */
export const fetchModuleGrants = async (
  token: string,
  userId?: string
): Promise<string[] | null | undefined> => {
  const id = userId ?? userIdFromToken(token);
  if (!id) return undefined;
  try {
    const res = await api.get(`/user-permissions/${id}`, { headers: { Authorization: token } });
    const body = res?.data;
    if (body?.status === false) return undefined;
    const mainModules = body?.data?.main_modules ?? body?.main_modules;
    return Array.isArray(mainModules) ? menuGrantsFromTree(mainModules) : undefined;
  } catch {
    return undefined;
  }
};

export const readStoredModuleGrants = (): string[] | null => {
  try {
    const stored = localStorage.getItem(MODULE_GRANTS_KEY);
    const parsed = stored ? JSON.parse(stored) : null;
    return Array.isArray(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export const storeModuleGrants = (grants: string[] | null) => {
  try {
    if (grants) localStorage.setItem(MODULE_GRANTS_KEY, JSON.stringify(grants));
    else localStorage.removeItem(MODULE_GRANTS_KEY);
  } catch {
    /* not worth failing the session over */
  }
};
